import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { updateTou, patchUnitSettings } from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {Switch} from "@/components/ui/switch";
import {Label} from "@/components/ui/label";
import { TimezoneSelect } from "@/components/TimezoneSelect";

interface Schedule {
  day: string;
  start: string;
  end: string;
}

interface TouEditorProps {
  unitId: string;
  touEnabled?: boolean;
  touSchedules?: Schedule[];
  // The unit's configured timezone. ToU schedule windows below are interpreted
  // in this zone (it does NOT affect how the dashboard displays times, which
  // always use the viewer's browser timezone).
  timezone?: string;
  onSuccess?: () => void;
  // When the unit:meta config mirror is missing, saving would overwrite stored
  // settings with defaults (backend returns 409). Disable the editor's Save.
  configUnavailable?: boolean;
}

const EMPTY_SCHEDULE: Schedule = { day: "all", start: "00:00", end: "06:00" };

export function TouEditor({ unitId, touEnabled = false, touSchedules = [], timezone = "", onSuccess, configUnavailable = false }: TouEditorProps) {
  const { isFree } = useAuth();
  const [enabled, setEnabled] = useState(touEnabled);
  const [schedules, setSchedules] = useState<Schedule[]>(touSchedules.length ? touSchedules : [{ ...EMPTY_SCHEDULE }]);
  const [tz, setTz] = useState(timezone);

  const maxSchedules = isFree ? 1 : 2;

  const mutation = useMutation({
    mutationFn: () => updateTou(unitId, enabled, schedules),
    onSuccess: () => {
      toast.success("TOU schedule updated");
      onSuccess?.();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update TOU"),
  });

  const tzM = useMutation({
    mutationFn: (value: string) => patchUnitSettings(unitId, { timezone: value }),
    onSuccess: () => {
      toast.success("ToU timezone updated");
      onSuccess?.();
    },
    onError: (e: any) => toast.error(e?.message || "Timezone update failed"),
  });

  function updateSchedule(idx: number, field: keyof Schedule, value: string) {
    setSchedules((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  }

  function addSchedule() {
    if (schedules.length >= maxSchedules) return;
    setSchedules((prev) => [...prev, { day: "weekday", start: "00:00", end: "06:00" }]);
  }

  function removeSchedule(idx: number) {
    setSchedules((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>TOU Schedule</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>ToU timezone</Label>
            <div className="flex gap-2">
              <TimezoneSelect value={tz} onChange={setTz} />
              <Button
                variant="outline"
                onClick={() => tzM.mutate(tz)}
                disabled={tzM.isPending || !tz || configUnavailable}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Used to interpret the schedule windows below. Times shown elsewhere in
              the dashboard use your browser's local timezone, not this setting.
            </p>
          </div>

          <Label>Enable TOU scheduling</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled}/>

          {enabled && (
            <>
              {schedules.map((s, idx) => (
                <div key={idx} className="flex flex-wrap items-end gap-3 p-3 rounded-xl bg-card/50">
                  <div>
                    <label className="text-sm font-medium text-xs">Day</label>
                    <Select value={s.day} onValueChange={(v) => updateSchedule(idx, "day", v)} disabled={isFree}>
                      <SelectTrigger size="sm">
                        <SelectValue placeholder="All Days" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Days</SelectItem>
                        {!isFree && (
                          <>
                            <SelectItem value="weekday">Weekday</SelectItem>
                            <SelectItem value="weekend">Weekend</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-xs">Start</label>
                    <input
                      type="time"
                      className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      value={s.start}
                      onChange={(e) => updateSchedule(idx, "start", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-xs">End</label>
                    <input
                      type="time"
                      className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      value={s.end}
                      onChange={(e) => updateSchedule(idx, "end", e.target.value)}
                    />
                  </div>
                  {schedules.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeSchedule(idx)}>
                      Remove
                    </Button>
                  )}
                </div>
              ))}

              {schedules.length < maxSchedules && (
                <Button variant="outline" size="sm" onClick={addSchedule}>
                  + Add Schedule
                </Button>
              )}

              {isFree && (
                <div className="text-xs text-warning">
                  Free tier: 1 schedule, "All Days" only. Upgrade to Prime for weekday/weekend schedules.
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-end gap-3">
          {configUnavailable && (
            <span className="text-xs text-red-600">Configuration unavailable — saving disabled</span>
          )}
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || configUnavailable}>
            {mutation.isPending ? "Saving..." : "Save TOU"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
